import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import { API } from "@/lib/api";

// Estimate read time from plain-text/HTML content. 220 WPM is the standard for
// conversational blog content; minimum 1 min.
const estimateReadMinutes = (content) => {
  if (!content) return 1;
  const text = String(content).replace(/<[^>]*>/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

const BlogPage = () => {
  const seoOverride = usePageSeo("blog");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (err) {
      console.warn("[BlogPage] Failed to load blog posts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="pt-20" data-testid="blog-page">
      <SEO
        title="Blog — Insights & Reflections"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Articles on mental health, trauma healing, anxiety, depression, and the transformative power of Ketamine-Assisted Psychotherapy by Laura A. Jeanis, LCSW."}
        imageAlt={seoOverride?.image_alt}
        pathname="/blog"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              Insights & <span className="italic text-primary">Reflections</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Explore articles on mental health, trauma healing, and the transformative 
              power of Ketamine-Assisted Psychotherapy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading articles...</p>
            </div>
          )}
          {!loading && posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No articles yet. Check back soon!</p>
            </div>
          )}
          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
                    <Card className="h-full bg-white border-border/40 hover:border-primary/30 rounded-2xl overflow-hidden group transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-hover">
                      {post.image_url && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={post.image_url}
                            alt={post.image_alt || post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="flex items-center gap-1" data-testid={`blog-card-readtime-${post.slug}`}>
                            <Clock className="h-4 w-4" />
                            {estimateReadMinutes(post.content)} min read
                          </span>
                        </div>
                        <h2 className="font-serif text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="mt-4 flex items-center text-primary font-medium text-sm">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
